import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import PageHeader from '@/components/layout/PageHeader';
import WineScanner from '@/components/wine/WineScanner';
import WineSearch from '@/components/wine/WineSearch';
import WineScanResult from '@/components/wine/WineScanResult';
import { useWineScanner } from '@/hooks/useWineScanner';
import type { ScannedWine } from '@/hooks/useWineScanner';
import type { WineSummary } from '@/api/wines';

type Step = 'choose' | 'scan' | 'manual' | 'result';

export default function AddWine() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const preselectedWineId = params.get('wineId');

  const [step, setStep] = useState<Step>(preselectedWineId ? 'result' : 'choose');
  const [scanTab, setScanTab] = useState<'bottle' | 'label'>('label');
  const [saveError, setSaveError] = useState('');
  const { scanning, scannedWine, error: scanError, scan, reset, saveTasting } = useWineScanner();

  async function handleCapture(file: File) {
    console.log('[AddWine] handleCapture', { scanTab });
    setStep('result');
    await scan(file, scanTab);
    console.log('[AddWine] scan() resolved');
  }

  function handleManualSelect(wine: WineSummary) {
    setStep('result');
    reset({
      wineId: wine.id || crypto.randomUUID(),
      name: wine.name,
      producer: wine.producer ?? null,
      vintage: wine.vintage ?? null,
      region: wine.region ?? null,
      country: null,
      grapes: [],
      style: wine.style ?? null,
      confidence: wine.id ? 'high' : 'low',
      rawOcrText: '',
    });
  }

  async function handleSave(wine: ScannedWine, rating: number, notes: string, addToCellar: boolean, tastedAt: string) {
    setSaveError('');
    try {
      await saveTasting(wine, rating, notes, addToCellar, tastedAt);
      await queryClient.invalidateQueries({ queryKey: ['recent-tastings'] });
      await queryClient.invalidateQueries({ queryKey: ['cellar'] });
      navigate('/');
    } catch (err) {
      console.error('[AddWine] save failed', err);
      setSaveError(err instanceof Error ? err.message : 'Save failed — check console for details');
    }
  }

  return (
    <div className="page page-add">
      <PageHeader title="Add a wine" back />

      {step === 'choose' && (
        <div className="add-choose">
          <div className="scan-tab-toggle">
            <button
              className={`scan-tab ${scanTab === 'label' ? 'active' : ''}`}
              onClick={() => { setScanTab('label'); setStep('scan'); }}
            >
              📷 Scan label
            </button>
            <button
              className={`scan-tab ${scanTab === 'bottle' ? 'active' : ''}`}
              onClick={() => { setScanTab('bottle'); setStep('scan'); }}
            >
              📷 Scan bottle
            </button>
            <button
              className="scan-tab"
              onClick={() => setStep('manual')}
            >
              🔍 Search by name
            </button>
          </div>
        </div>
      )}

      {step === 'scan' && (
        <div className="add-scan">
          <p className="add-instruction">
            Point camera at the {scanTab === 'label' ? 'label' : 'bottle'}
          </p>
          <WineScanner onCapture={handleCapture} label={`Scan ${scanTab}`} />
          {scanning && <p className="add-scanning">Reading label…</p>}
          <button className="add-back-link" onClick={() => setStep('choose')}>
            ← Choose differently
          </button>
        </div>
      )}

      {step === 'manual' && (
        <div className="add-manual">
          <p className="add-instruction">Search for the wine</p>
          <WineSearch onSelect={handleManualSelect} />
          <button className="add-back-link" onClick={() => setStep('choose')}>
            ← Choose differently
          </button>
        </div>
      )}

      {step === 'result' && scanning && (
        <p className="add-scanning">Reading label…</p>
      )}

      {step === 'result' && !scanning && scanError && (
        <div className="add-scan-error">
          <p>{scanError}</p>
          <button className="add-back-link" onClick={() => { reset(null); setStep('choose'); }}>
            ← Try again
          </button>
        </div>
      )}

      {step === 'result' && scannedWine && (
        <>
          {saveError && <p className="form-error" style={{ padding: '0 var(--space-4)' }}>{saveError}</p>}
          <WineScanResult
            wine={scannedWine}
            onSave={handleSave}
            onRetry={() => { reset(null); setStep('choose'); setSaveError(''); }}
          />
        </>
      )}
    </div>
  );
}
